import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { BoltIcon } from "./Bolts";
import { CATEGORIES, CONTACT_EMAIL, ORDER_STORAGE_KEY } from "../data/catalog";
import { Mail, MapPin, Send, Instagram, Facebook, ShoppingBag } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Footer = () => {
    const location = useLocation();
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const [pendingOrder, setPendingOrder] = useState(null);
    const messageRef = useRef(null);

    // Pull any pending order injected from a product card click
    const consumePendingOrder = () => {
        try {
            const raw = sessionStorage.getItem(ORDER_STORAGE_KEY);
            if (!raw) return;
            const order = JSON.parse(raw);
            sessionStorage.removeItem(ORDER_STORAGE_KEY);
            setForm((f) => ({ ...f, message: order.message || f.message }));
            setPendingOrder(order);
            // Focus the name field so they can complete checkout
            setTimeout(() => {
                document.getElementById("contact-us-now")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 60);
        } catch {}
    };

    useEffect(() => {
        consumePendingOrder();
        const handler = () => consumePendingOrder();
        window.addEventListener("hashchange", handler);
        window.addEventListener("dsc:order-placed", handler);
        return () => {
            window.removeEventListener("hashchange", handler);
            window.removeEventListener("dsc:order-placed", handler);
        };
    }, [location.pathname]);

    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error("Please fill in your name, email, and message.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/contacts`, {
                ...form,
                page: pendingOrder ? `ORDER · ${location.pathname}` : location.pathname,
            });
            toast.success(
                pendingOrder
                    ? "Order received. We'll confirm by email shortly."
                    : "Message sent. We'll get back to you soon."
            );
            setForm({ name: "", email: "", message: "" });
            setPendingOrder(null);
        } catch (err) {
            const detail = err?.response?.data?.detail || "Could not send right now. Try again.";
            toast.error(typeof detail === "string" ? detail : "Could not send right now. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <footer data-testid="site-footer" className="relative border-t border-white/10 bg-storm-void">
            {/* Contact Us Now CTA (on every page) */}
            <section
                id="contact-us-now"
                data-testid="contact-us-now-section"
                className="relative overflow-hidden scroll-mt-24"
            >
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-storm-bolt/10 blur-3xl rounded-full" />
                </div>
                <div className="container-x relative z-10 py-20 md:py-28">
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
                        <div>
                            <p className="eyebrow flex items-center gap-2 mb-4">
                                <BoltIcon className="w-3.5 h-3.5" /> Contact Us Now
                            </p>
                            <h2 className="text-4xl md:text-5xl lg:text-6xl text-white mb-6">
                                Custom colors. <br />
                                Custom designs. <br />
                                <span className="text-storm-bolt">Custom anything.</span>
                            </h2>
                            <p className="text-storm-mist max-w-md text-base md:text-lg leading-relaxed mb-8">
                                Got a wild idea? A specific color combo? A wood species in mind?
                                A logo you need brought to life? Drop us a line — every custom
                                build starts with a conversation.
                            </p>
                            <div className="space-y-3 text-sm">
                                <a
                                    href={`mailto:${CONTACT_EMAIL}`}
                                    className="flex items-center gap-3 text-white/80 hover:text-storm-bolt transition-colors"
                                    data-testid="footer-email-link"
                                >
                                    <Mail className="w-4 h-4 text-storm-bolt" />
                                    {CONTACT_EMAIL}
                                </a>
                                <div className="flex items-center gap-3 text-white/80">
                                    <MapPin className="w-4 h-4 text-storm-bolt" />
                                    Made to order · Ships worldwide
                                </div>
                            </div>
                        </div>

                        <form
                            onSubmit={onSubmit}
                            data-testid="footer-contact-form"
                            className="surface-solid p-6 md:p-8 space-y-5"
                        >
                            {pendingOrder && (
                                <div
                                    data-testid="pending-order-banner"
                                    className="border border-storm-bolt/60 bg-storm-bolt/10 p-4 flex items-start gap-3"
                                >
                                    <ShoppingBag className="w-5 h-5 text-storm-bolt mt-0.5 flex-shrink-0" />
                                    <div className="flex-1">
                                        <p className="font-heading uppercase tracking-wider text-storm-bolt text-sm">
                                            Order ready · ${pendingOrder.total}
                                        </p>
                                        <p className="text-xs text-white mt-1">
                                            {pendingOrder.title}
                                            {pendingOrder.adultSize && ` · Adult ${pendingOrder.adultSize}`}
                                            {pendingOrder.kidsSize && ` · Kids ${pendingOrder.kidsSize}`}
                                        </p>
                                        <p className="text-[11px] text-storm-mist mt-1">
                                            Add your name &amp; email below and we'll confirm by email and send a payment link.
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setPendingOrder(null);
                                            setForm((f) => ({ ...f, message: "" }));
                                        }}
                                        className="text-xs uppercase tracking-[0.2em] text-storm-mist hover:text-storm-bolt"
                                        data-testid="clear-pending-order"
                                    >
                                        Clear
                                    </button>
                                </div>
                            )}
                            <h3 className="text-2xl text-white">
                                {pendingOrder ? "Complete your order" : "Tell us what you want"}
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-[11px] uppercase tracking-[0.2em] text-storm-mist block mb-2">
                                        Your Name
                                    </label>
                                    <Input
                                        name="name"
                                        value={form.name}
                                        onChange={onChange}
                                        placeholder="Jane Storm"
                                        data-testid="footer-input-name"
                                        className="bg-storm-void border-white/10 text-white placeholder:text-storm-mist/60 rounded-none h-12 focus-visible:ring-storm-bolt focus-visible:ring-offset-0 focus-visible:border-storm-bolt"
                                    />
                                </div>
                                <div>
                                    <label className="text-[11px] uppercase tracking-[0.2em] text-storm-mist block mb-2">
                                        Email
                                    </label>
                                    <Input
                                        name="email"
                                        type="email"
                                        value={form.email}
                                        onChange={onChange}
                                        placeholder="you@example.com"
                                        data-testid="footer-input-email"
                                        className="bg-storm-void border-white/10 text-white placeholder:text-storm-mist/60 rounded-none h-12 focus-visible:ring-storm-bolt focus-visible:ring-offset-0 focus-visible:border-storm-bolt"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="text-[11px] uppercase tracking-[0.2em] text-storm-mist block mb-2">
                                    Custom Ideas, Colors, Sizes — Anything
                                </label>
                                <Textarea
                                    name="message"
                                    value={form.message}
                                    onChange={onChange}
                                    rows={5}
                                    placeholder="Tell us what you're thinking — custom colors, sizes, designs, materials, anything goes."
                                    data-testid="footer-input-message"
                                    className="bg-storm-void border-white/10 text-white placeholder:text-storm-mist/60 rounded-none focus-visible:ring-storm-bolt focus-visible:ring-offset-0 focus-visible:border-storm-bolt resize-none"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="footer-submit-message"
                                className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                <Send className="w-4 h-4" />
                                {submitting ? "Sending…" : "Contact Us Now"}
                            </button>
                        </form>
                    </div>
                </div>
            </section>

            {/* Bottom bar */}
            <div className="border-t border-white/10 bg-storm-navy">
                <div className="container-x py-10 grid grid-cols-1 md:grid-cols-12 gap-8">
                    <div className="md:col-span-4">
                        <Link to="/" className="flex items-center gap-2">
                            <span className="inline-flex items-center justify-center w-8 h-8 bg-storm-bolt text-black">
                                <BoltIcon className="w-4 h-4" />
                            </span>
                            <span className="font-heading text-lg tracking-tight">
                                <span className="text-white">DANGER</span>
                                <span className="text-storm-bolt">SKY</span>
                                <span className="text-white"> COLLECTIBLES</span>
                            </span>
                        </Link>
                        <p className="text-sm text-storm-mist mt-4 max-w-sm">
                            Storm-born custom apparel, stickers, woodworking, and laser
                            engraving. Made by hand. Built to last.
                        </p>
                    </div>

                    <div className="md:col-span-5 grid grid-cols-2 sm:grid-cols-3 gap-6">
                        {CATEGORIES.slice(0, 3).map((cat) => (
                            <div key={cat.slug}>
                                <div className="eyebrow mb-3">{cat.name}</div>
                                <ul className="space-y-1.5 text-sm text-white/80">
                                    <li>
                                        <Link to={`/${cat.slug}`} className="hover:text-storm-bolt">
                                            All {cat.name}
                                        </Link>
                                    </li>
                                    {cat.sub.slice(0, 4).map((s) => (
                                        <li key={s.slug}>
                                            <Link
                                                to={`/${cat.slug}/${s.slug}`}
                                                className="hover:text-storm-bolt"
                                            >
                                                {s.name}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    <div className="md:col-span-3">
                        <div className="eyebrow mb-3">Connect</div>
                        <ul className="space-y-2 text-sm text-white/80">
                            <li>
                                <a href="#contact-us-now" className="hover:text-storm-bolt">
                                    Contact Us Now
                                </a>
                            </li>
                            <li>
                                <Link to="/contact" className="hover:text-storm-bolt">
                                    Contact Page
                                </Link>
                            </li>
                        </ul>
                        <div className="flex items-center gap-3 mt-5">
                            <a
                                href="#"
                                aria-label="Instagram"
                                className="w-9 h-9 inline-flex items-center justify-center border border-white/15 hover:border-storm-bolt hover:text-storm-bolt transition-colors"
                            >
                                <Instagram className="w-4 h-4" />
                            </a>
                            <a
                                href="#"
                                aria-label="Facebook"
                                className="w-9 h-9 inline-flex items-center justify-center border border-white/15 hover:border-storm-bolt hover:text-storm-bolt transition-colors"
                            >
                                <Facebook className="w-4 h-4" />
                            </a>
                        </div>
                    </div>
                </div>
                <div className="border-t border-white/10">
                    <div className="container-x py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-storm-mist">
                        <p>© {new Date().getFullYear()} DangerSky. All rights reserved.</p>
                        <p className="font-mono tracking-wider">Built by Claude (Anthropic) & Justin</p>
                        <p className="font-mono tracking-wider">dangerskycollectibles.com</p>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
